import { ClipboardList } from "lucide-react";
import { FeedbackMediaThumbnail } from "@/features/coach-review/components/FeedbackMediaThumbnail";

/** A note's display title — the observation-aware fallback chain.
 * Re-exported from `feedbackHelpers` (the tested source of truth)
 * so card components can keep importing it from here. */
export { noteDisplayTitle as noteTitle } from "../feedbackHelpers";

/**
 * Small shared pieces for the `/feedback` viewer cards — the
 * access-controlled thumbnail tile and the tone pill.
 *
 * Both port directly from legacy `js/coaching/feedback.js`:
 *   - `FeedbackThumb` wraps the shared Bearer-auth blob thumbnail
 *     tile — never direct `<img src="/api/...">` URLs.
 *   - `FeedbackTonePill` mirrors `_feedbackTonePillHtml` +
 *     `FEEDBACK_NOTE_TYPE_LABELS` (the player-friendly tone
 *     labels — longer than the Coach Review composer's chips).
 */

/** Player-facing tone labels — same key set as `_VALID_NOTE_TYPES`. */
const TONE_LABELS: Record<string, string> = {
  positive: "Positive",
  correction: "Correction",
  question: "Question",
  team_concept: "Team concept",
  individual_goal: "Individual goal",
};

export function FeedbackTonePill({ noteType }: { noteType?: string | null }) {
  const label = noteType ? TONE_LABELS[noteType] : undefined;
  if (!label) return null;
  return (
    <span
      data-feedback-tone={noteType}
      data-testid="feedback-tone-pill"
      className="rounded px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wider bg-surface text-ink-muted"
    >
      {label}
    </span>
  );
}

/** Access-controlled thumbnail tile for a note or clip — the JPEG
 * via the M5 `useThumbnail` primitive; a film-strip placeholder
 * until it resolves (or on a 403/404 — a viewer who can't see the
 * source just gets the placeholder, never a broken image).
 *
 * `hasThumbnail` (#210) — pass `false` for notes that don't yet have
 * a JPEG on disk (the backend stamps `note.has_thumbnail` on the
 * list endpoint). The thumb still mounts but skips the GET so a
 * Feedback Library full of thumbnail-less notes doesn't fan out
 * 6+ failed network calls per page load. Defaults to `true` for
 * back-compat with callers that don't yet know the flag. */
export function FeedbackThumb({
  kind,
  id,
  hasThumbnail = true,
}: {
  kind: "note" | "clip";
  id: number;
  hasThumbnail?: boolean;
}) {
  return (
    <FeedbackMediaThumbnail
      kind={kind}
      id={id}
      enabled={hasThumbnail}
      testId="feedback-thumb"
      placeholderTestId="feedback-thumb-placeholder"
      className="block h-12 w-20 shrink-0 overflow-hidden rounded border border-rule/60 bg-gradient-to-br from-canvas to-surface"
    />
  );
}

/** A static placeholder tile for an observation note with no
 * tactical board — the clipboard glyph, no network request.
 * (An observation WITH a board renders a board chip instead;
 * that lands with the board-preview work, not this slice — for
 * now a board-bearing observation also shows this glyph.) */
export function ObservationThumb() {
  return (
    <span
      className="flex h-12 w-20 shrink-0 items-center justify-center rounded bg-surface border border-rule/60 text-ink-muted"
      aria-hidden="true"
      data-testid="feedback-observation-thumb"
    >
      <ClipboardList size={16} strokeWidth={2} aria-hidden="true" />
    </span>
  );
}
